// @ts-nocheck
import React, { FC, Fragment } from 'react';
import { useForm } from 'react-hook-form';

import TextField from 'crehana-ui/TextField';
import PrimaryButton from 'crehana-ui/Buttons/PrimaryButton';
import TextLink from 'crehana-ui/Buttons/TextLink';
import Loader from 'crehana-ui/Loader';

import { useAuth } from 'Jsx/ContextApis/AuthContext';
import { REGEX_EMAIL } from 'Jsx/Shared/regex';
import captureSentryMessage from 'Jsx/Utils/captureSentryMessage';
import customLocalStorage from 'Jsx/Utils/customLocalStorage';
import customSessionStorage from 'Jsx/Utils/customSessionStorage';

import {
  useValidateCouponMutation,
  useValidateEmailLazyQuery,
} from 'Jsx/CouponLanding/hooks';
import useCLTranslation from 'Jsx/CouponLanding/hooks/useCLTranslation';

import { Maybe } from '../../../Types/__generated__/types';

type ErrorMessages = Array<
  Maybe<{
    __typename?: 'MessageNode';
    field?: Maybe<string>;
    message?: Maybe<string>;
  }>
>;

interface FormValues {
  email: string;
  password: string;
  coupon: string;
}

const validations = {
  required: { value: true, message: 'FORM_REQUIRED' },
  maxLength: { value: 300, message: 'FORM_MAX_LENGTH' },
  minLength: { value: 6, message: 'FORM_MIN_LENGTH' },
  email: { value: REGEX_EMAIL, message: 'FORM_EMAIL_VALID' },
};

interface CouponAuthFormProps {
  landingId: string;
  buttonLabel: string;
  nextUrl: string;
  promoImageUrl?: string;
  couponCode: string;
  itemId: number;
  itemType: 'membership' | 'course';
}

const CouponAuthForm: FC<CouponAuthFormProps> = ({
  landingId,
  buttonLabel,
  nextUrl,
  promoImageUrl,
  couponCode,
  itemId,
  itemType,
}) => {
  const { t } = useCLTranslation();
  const {
    data: { me, loading, refetch: refetchUser },
    register: registerUser,
    login: loginUser,
  } = useAuth();
  const { register, errors, handleSubmit, setError, formState } =
    useForm<FormValues>({
      mode: 'onChange',
      defaultValues: { email: '', password: '', coupon: couponCode },
    });
  const [validateEmailQuery, { data: validateEmailData }] =
    useValidateEmailLazyQuery({
      fetchPolicy: 'cache-first',
    });
  const [validateCoupon] = useValidateCouponMutation();

  const isLoggedUser = !!me;
  const shouldShowAuth = !loading && !isLoggedUser;
  const userApplyForCoupon = !isLoggedUser;

  const validateEmail = (value: string) => {
    validateEmailQuery({ variables: { value } });

    return true;
  };

  const onSubmit = handleSubmit(async ({ email, password, coupon }) => {
    try {
      const isSignin = validateEmailData?.verifyUser?.success === false;
      let authSuccess: boolean | null | undefined;
      let authErrors: ErrorMessages = [];

      if (isSignin) {
        const { data } = await loginUser({
          username: email,
          password,
          nextUrl: '',
        });

        authSuccess = data?.loginUser?.success;
        authErrors = data?.loginUser?.messages || [];
      } else {
        const { data } = await registerUser({ email, password, nextUrl: '' });

        authSuccess = data?.createUser?.success;
        authErrors = data?.createUser?.messages || [];
      }

      if (authSuccess) {
        const variables = {
          landingId: landingId.toString(),
          coupon,
          itemType,
          itemId: itemId.toString(),
        };

        const { data: validateData } = await validateCoupon({ variables });

        if (validateData?.validCoupon?.success) {
          // We need this data to handle onboarding redirection, the image
          // is displayed in the thanks dialog.
          const data = {
            landingId: landingId.toString(),
            nextUrl,
            itemType,
            itemId: itemId.toString(),
            coupon,
            promoImageUrl,
            claimed: false,
          };

          customLocalStorage.setItem(
            'COUPON_LANDING__COUPON_DATA',
            JSON.stringify(data),
          );
          try {
            customSessionStorage.setItem(
              'ONBOARDING_USER_FORM_DATA',
              JSON.stringify({
                title: 'USER_FORM_COUPON_TITLE',
              }),
            );
          } catch (e) {
            console.error(e);
          }
          window.location.href = '/registro-extra/';
        } else {
          setError('coupon', {
            message: validateData?.validCoupon?.response?.message || '',
            type: '',
          });
        }

        // if login success and coupon fails, prevent user for login in again
        if (!isLoggedUser) {
          refetchUser();
        }
      } else {
        authErrors.forEach(err => {
          const fieldName = err?.field;
          const message = err?.message || '';

          if (fieldName === 'all') {
            setError('password', { message, type: '' });
          } else if (fieldName === 'username' || fieldName === 'email') {
            setError('email', { message, type: '' });
          } else if (fieldName === 'password') {
            setError('password', { message, type: '' });
          }
        });
      }
    } catch (error) {
      setError('coupon', {
        message: 'Ocurrió un error al validar el cupon',
        type: '',
      });
      captureSentryMessage('CouponLanding - Failed to claim coupon', {
        user: me
          ? {
              originalId: me.originalId,
              username: me.username,
            }
          : undefined,
      });
    }
  });

  const isDisabled = loading || formState.isSubmitting;

  if (!userApplyForCoupon) {
    return null;
  }

  return (
    <Fragment>
      <form onSubmit={onSubmit} className="mb-24">
        {shouldShowAuth ? (
          <Fragment>
            <TextField
              label={t('FORM_EMAIL_LABEL')}
              darkMode
              full
              disabled={isDisabled}
              className="mb-12 sm:mb-16"
              type="email"
              name="email"
              ref={register({
                required: validations.required,
                pattern: validations.email,
                validate: validateEmail,
              })}
              errorText={errors.email?.message && t(errors.email.message)}
            />
            <TextField
              label={t('FORM_PASSWORD_LABEL')}
              darkMode
              full
              disabled={isDisabled}
              className="mb-12 sm:mb-16"
              type="password"
              name="password"
              ref={register({
                required: validations.required,
                minLength: validations.minLength,
              })}
              errorText={errors.password?.message && t(errors.password.message)}
              autoComplete="new-password"
            />
          </Fragment>
        ) : null}

        <TextField
          label={t('FORM_COUPON_LABEL')}
          darkMode
          full
          disabled={isDisabled}
          className="mb-20 sm:mb-24"
          name="coupon"
          ref={register({
            required: validations.required,
          })}
          errorText={errors.coupon?.message && t(errors.coupon.message)}
        />

        <PrimaryButton
          type="submit"
          size="l"
          label={buttonLabel}
          darkMode
          full
          disabled={!formState.isValid || isDisabled}
        />
      </form>

      <small className="block text-center font-legals text-base-lighter">
        {t('FORM_TERMS_1')}
        <br />
        <TextLink
          label={t('FORM_TERMS_2')}
          type="legals"
          darkMode
          className="text-inherit"
          href="/legal/terminos-y-condiciones/"
          target="_blank"
          rel="noopener noreferrer"
        />
      </small>
    </Fragment>
  );
};

export default CouponAuthForm;
